import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["children"];
import PropTypes from 'prop-types';
import React, { forwardRef, useRef } from 'react';
import { ICON_SIZES } from '../../constants';
import { identifier } from '../../util/identifier';
import { withIcon } from '../Icon/Icon';
import { StyledSpinner } from './Spinner.styles';

/**
 * A visual indicator that something is loading, submitting, precessing, etc.
 *
 * You should include this in a container with `aria-role="status"` and supporting text to help
 * assistive technologies announce what is happening; the `LoadingMask` and `LoadingButton`
 * components do this for you.
 *
 * @related ['Combobox', 'LoadingButton', 'LoadingMask']
 * @since 8.1.0
 */
export var Spinner = withIcon( /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var children = _ref.children,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useRef = useRef(identifier()),
    id = _useRef.current;
  return /*#__PURE__*/React.createElement(StyledSpinner, _extends({
    ref: forwardedRef,
    viewBox: "0 0 10 10"
  }, rest), children, /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("linearGradient", {
    id: id + "_a",
    x1: ".5",
    y1: "0",
    x2: "0",
    y2: ".5"
  }, /*#__PURE__*/React.createElement("stop", {
    stopColor: "currentColor",
    stopOpacity: "1"
  }), /*#__PURE__*/React.createElement("stop", {
    stopColor: "currentColor",
    stopOpacity: ".75",
    offset: "1"
  })), /*#__PURE__*/React.createElement("linearGradient", {
    id: id + "_b",
    x1: "0",
    y1: ".5",
    x2: ".5",
    y2: "1"
  }, /*#__PURE__*/React.createElement("stop", {
    stopColor: "currentColor",
    stopOpacity: ".75"
  }), /*#__PURE__*/React.createElement("stop", {
    stopColor: "currentColor",
    stopOpacity: ".5",
    offset: "1"
  })), /*#__PURE__*/React.createElement("linearGradient", {
    id: id + "_c",
    x1: ".5",
    y1: "1",
    x2: "1",
    y2: ".5"
  }, /*#__PURE__*/React.createElement("stop", {
    stopColor: "currentColor",
    stopOpacity: ".5"
  }), /*#__PURE__*/React.createElement("stop", {
    stopColor: "currentColor",
    stopOpacity: ".25",
    offset: "1"
  })), /*#__PURE__*/React.createElement("linearGradient", {
    id: id + "_d",
    x1: "1",
    y1: ".5",
    x2: "0",
    y2: "0"
  }, /*#__PURE__*/React.createElement("stop", {
    stopColor: "currentColor",
    stopOpacity: ".25"
  }), /*#__PURE__*/React.createElement("stop", {
    stopColor: "currentColor",
    stopOpacity: "0",
    offset: "1"
  }))), /*#__PURE__*/React.createElement("g", {
    fill: "none",
    strokeWidth: "1.5"
  }, /*#__PURE__*/React.createElement("path", {
    stroke: "url(#" + id + "_a)",
    d: "M 5 1 A 4 4 0 0 0 1 5"
  }), /*#__PURE__*/React.createElement("path", {
    stroke: "url(#" + id + "_b)",
    d: "M 1 5 A 4 4 0 0 0 5 9"
  }), /*#__PURE__*/React.createElement("path", {
    stroke: "url(#" + id + "_c)",
    d: "M 5 9 A 4 4 0 0 0 9 5"
  }), /*#__PURE__*/React.createElement("path", {
    stroke: "url(#" + id + "_d)",
    d: "M 9 5 A 4 4 0 0 0 7 1.536"
  })), /*#__PURE__*/React.createElement("circle", {
    fill: "currentColor",
    cx: "5",
    cy: "1",
    r: ".25"
  }));
}));
Spinner.displayName = 'Spinner';
Spinner.propTypes = process.env.NODE_ENV !== "production" ? {
  children: PropTypes.node,
  /** @ignore */
  fontColor: PropTypes.string,
  /** The spinner size. By default this will match the font-size of the container. */
  size: PropTypes.oneOf(Object.values(ICON_SIZES))
} : {};
Spinner.defaultProps = {
  children: /*#__PURE__*/React.createElement("title", null, "Loading"),
  fontColor: 'brand',
  size: undefined
};